import { useCallback, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import z from 'zod';

const CONSENT_COOKIE_NAME = 'consent';
export type CookieScreen = 'cookie' | 'cookie-preference';
export type CookiePreferences = {
  performance: boolean;
  functional: boolean;
  targeting: boolean;
};
const CookiePreferencesAsCookieValue = z.object({
  performance: z.optional(z.union([z.literal('yes'), z.literal('no')])),
  functional: z.optional(z.union([z.literal('yes'), z.literal('no')])),
  targeting: z.optional(z.union([z.literal('yes'), z.literal('no')])),
});

type CookiePreferencesAsCookieValue = z.infer<
  typeof CookiePreferencesAsCookieValue
>;

function buildCookieValue(preferences: CookiePreferences) {
  const cookieProperties = {
    source: 'user',
    necessary: 'yes',
    available: 'yes',
    preference: 'yes',
    targeting: preferences.targeting ? 'yes' : 'no',
    functional: preferences.functional ? 'yes' : 'no',
    performance: preferences.performance ? 'yes' : 'no',
  };
  const base64Encoded = btoa(JSON.stringify(cookieProperties));
  return base64Encoded;
}

function getCookieProperties(
  cookie: CookiePreferencesAsCookieValue,
): CookiePreferences {
  return {
    performance: cookie?.performance === 'yes',
    targeting: cookie?.targeting === 'yes',
    functional: cookie?.functional === 'yes',
  };
}

function getDateAYearFromNow() {
  const now = new Date();
  return new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
}

export function useConsentCookie({
  turnOn,
  turnOff,
}: {
  turnOn: () => void;
  turnOff: () => void;
}) {
  const [cookies, setCookies] = useCookies([CONSENT_COOKIE_NAME]);
  const savePreferences = useCallback(
    (cookiePreferences: CookiePreferences) => {
      setCookies(CONSENT_COOKIE_NAME, buildCookieValue(cookiePreferences), {
        expires: getDateAYearFromNow(),
        path: '/',
      });
      if (cookiePreferences.performance) {
        turnOn();
      } else {
        turnOff();
      }
    },
    [setCookies, turnOff, turnOn],
  );
  const allowAll = useCallback(() => {
    savePreferences({ targeting: true, functional: true, performance: true });
    turnOn();
  }, [savePreferences]);

  const consentCookie = useMemo(() => {
    if (!cookies.consent) {
      return undefined;
    }

    try {
      return getCookieProperties(
        CookiePreferencesAsCookieValue.parse(JSON.parse(atob(cookies.consent))),
      );
    } catch {
      // Invalid / unexpected cookie format → treat as no consent
      return undefined;
    }
  }, [cookies.consent, turnOn]);

  return { consentCookie, savePreferences, allowAll };
}
